import {
    Badge,
    Box,
    HStack,
    Icon,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
} from '@chakra-ui/react';
import { TbInfoCircle } from 'react-icons/tb';
import PartType from '../PartType';
import SourceBadge from '../SourceBadge';
import VendorTypeIcon from '../VendorTypeIcon';
import { formatPrice } from '../../functions';

/**
 * Info-graphic part-options card injected into Ask Carl responses. Mirrors
 * PartOptionsModal's layout and iconography for pattern recognition: same
 * VendorTypeIcon, SourceBadge, PartType chips, insurer-best "i" / best-price
 * "$" mini squares, Valid column with info tooltip, and the blue selected-row
 * treatment — on the platform's light theme.
 */

export interface PartOptionRow {
    _id: string;
    supplierText: string;
    source?: 'ccc' | 'tesla' | 'partstrader' | 'opstrax' | 'ccc-sp' | 'inventory';
    type?: string;
    deliveryString?: string;
    qty: number;
    listPrice: number;
    adjustedListPrice?: number;
    listPriceAdjusted?: boolean;
    buyPrice: number;
    selected?: boolean;
    supplierPartNumber?: string;
    supplierNote?: string;
    supplierComment?: string;
    optionNote?: string;
    vendorType?: 'preferred' | 'avoid' | 'required' | 'super-preferred' | null;
    bestPrice?: boolean;
    insurerBest?: boolean;
    partbayInsuranceBest?: boolean;
    selectable?: boolean;
    validity?: { isValid: boolean; reason?: string; reasonKey?: string };
}

export interface PartOptionsSnapshotData {
    title?: string;
    partNumber?: string;
    correctedPartNumber?: string;
    roNumber?: string;
    insurance?: string | null;
    preOrder?: boolean;
    options?: PartOptionRow[];
    allOptions?: PartOptionRow[];
}

const marginFor = (option: PartOptionRow): number => {
    const basePrice = option.adjustedListPrice ?? option.listPrice;
    if (!basePrice) return 0;
    return ((basePrice - option.buyPrice) / basePrice) * 100;
};

/** Same mini flag squares the modal stacks before the vendor name. */
const OptionFlagSquares = ({ option }: { option: PartOptionRow }) => {
    if (!option.insurerBest && !option.bestPrice) return null;
    return (
        <VStack spacing={0.5} align="center">
            {option.insurerBest && (
                <Tooltip label="Insurer's best choice" placement="top" hasArrow>
                    <Box w="12px" h="12px" bg="green.500" borderRadius="4px" display="flex" alignItems="center" justifyContent="center">
                        <Text fontSize="8px" color="white" fontWeight="bold" lineHeight="1">i</Text>
                    </Box>
                </Tooltip>
            )}
            {option.bestPrice && (
                <Tooltip label="Best price" placement="top" hasArrow>
                    <Box w="12px" h="12px" bg="gray.800" borderRadius="4px" display="flex" alignItems="center" justifyContent="center">
                        <Text fontSize="8px" color="white" fontWeight="bold" lineHeight="1">$</Text>
                    </Box>
                </Tooltip>
            )}
        </VStack>
    );
};

const headerProps = { fontSize: '10px', color: 'gray.500', borderColor: 'gray.200', py: 3 } as const;
const cellProps = { fontSize: 'sm', color: 'gray.800', borderColor: 'gray.200', py: 3 } as const;

const PartOptionsCard = ({ partOptions }: { partOptions: PartOptionsSnapshotData; }) => {
    const options = (partOptions.allOptions?.length ? partOptions.allOptions : partOptions.options) ?? [];
    if (!options.length) return null;

    const sorted = [...options].sort((a, b) => Number(b.selected ?? false) - Number(a.selected ?? false));
    const bestMargin = Math.max(...options.map(marginFor));

    return (
        <Box border="1px solid" borderColor="gray.200" borderRadius="xl" bg="white" boxShadow="none" overflow="hidden">
            <Box px={3} py={2.5} borderBottom="1px solid" borderColor="gray.200">
                <HStack justify="space-between" align="flex-start">
                    <VStack spacing={0.5} align="flex-start">
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                            {partOptions.title || 'Part Options'}
                        </Text>
                        <HStack spacing={2} flexWrap="wrap">
                            {partOptions.partNumber && (
                                <Text fontSize="xs" color="gray.500">OE #: {partOptions.partNumber}</Text>
                            )}
                            {partOptions.correctedPartNumber && (
                                <Text fontSize="xs" color="gray.500">Corrected: {partOptions.correctedPartNumber}</Text>
                            )}
                            {partOptions.roNumber && (
                                <Text fontSize="xs" color="gray.500">RO #{partOptions.roNumber}</Text>
                            )}
                            {partOptions.insurance && (
                                <Text fontSize="xs" color="gray.500">Insurance: {partOptions.insurance}</Text>
                            )}
                        </HStack>
                    </VStack>
                    <HStack spacing={1.5}>
                        {partOptions.preOrder && (
                            <Badge colorScheme="yellow" variant="subtle" fontSize="10px">PRE-ORDER</Badge>
                        )}
                        <Badge colorScheme="blue" variant="subtle" fontSize="10px">
                            {options.length} option{options.length === 1 ? '' : 's'}
                        </Badge>
                    </HStack>
                </HStack>
            </Box>
            <TableContainer maxH="380px" overflowY="auto">
                <Table size="sm" variant="simple">
                    <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
                        <Tr>
                            <Th {...headerProps}>Vendor Name</Th>
                            <Th {...headerProps}>Source</Th>
                            <Th {...headerProps}>Valid</Th>
                            <Th {...headerProps}>Delivery</Th>
                            <Th {...headerProps} isNumeric>Qty</Th>
                            <Th {...headerProps} isNumeric>List Price</Th>
                            <Th {...headerProps} isNumeric>Adj. List Price</Th>
                            <Th {...headerProps} isNumeric>Buy Price</Th>
                            <Th {...headerProps} isNumeric>Gross Margin</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {sorted.map((option) => {
                            const isSelected = option.selected === true;
                            const isValid = option.validity ? option.validity.isValid !== false : true;
                            const invalidReason = !isValid ? option.validity?.reason : null;
                            const supplierNoteBody = (option.supplierNote ?? '').replace(/^Part #:\s*.+?(\n|$)/, '') || option.supplierComment || option.optionNote || '';
                            const optionMargin = marginFor(option);
                            const isBestMargin = optionMargin === bestMargin && options.length > 1;
                            const unselectable = option.selectable === false;

                            return (
                                <Tr
                                    key={option._id}
                                    bg={isSelected ? 'blue.50' : 'transparent'}
                                    borderLeft={isSelected ? '4px solid' : '4px solid transparent'}
                                    sx={isSelected ? { borderLeftColor: 'var(--chakra-colors-blue-500)' } : undefined}
                                    opacity={unselectable || !isValid ? 0.6 : 1}
                                >
                                    <Td {...cellProps} color={isSelected ? 'gray.900' : 'gray.800'}>
                                        <VStack align="flex-start" spacing={1}>
                                            <HStack spacing={2} align="center">
                                                <OptionFlagSquares option={option} />
                                                {option.vendorType && <VendorTypeIcon type={option.vendorType} />}
                                                <Text fontWeight={isSelected ? 'semibold' : 'normal'} whiteSpace="nowrap">
                                                    {option.supplierText}
                                                </Text>
                                                {option.type && <PartType partType={option.type} />}
                                                {(supplierNoteBody || option.supplierPartNumber) && (
                                                    <Tooltip
                                                        label={`${option.supplierPartNumber ? `Part #: ${option.supplierPartNumber}` : ''}${option.supplierPartNumber && supplierNoteBody ? '\n' : ''}${supplierNoteBody}`}
                                                        placement="top"
                                                        hasArrow
                                                        maxW="320px"
                                                        whiteSpace="pre-wrap"
                                                    >
                                                        <Box display="inline-flex" alignItems="center">
                                                            <Icon as={TbInfoCircle as React.ElementType} boxSize={3.5} color="gray.400" _hover={{ color: 'gray.600' }} />
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                                {isSelected && (
                                                    <Badge colorScheme="blue" variant="subtle" fontSize="9px">SELECTED</Badge>
                                                )}
                                            </HStack>
                                        </VStack>
                                    </Td>
                                    <Td {...cellProps}>
                                        {option.source ? <SourceBadge source={option.source} /> : '—'}
                                    </Td>
                                    <Td {...cellProps}>
                                        <HStack spacing={1} align="center">
                                            <Text fontSize="sm" color={isValid ? 'gray.800' : 'red.500'}>
                                                {isValid ? 'Yes' : 'No'}
                                            </Text>
                                            {!isValid && invalidReason && (
                                                <Tooltip label={invalidReason} placement="top" hasArrow>
                                                    <Box display="inline-flex" alignItems="center">
                                                        <Icon as={TbInfoCircle as React.ElementType} w={3.5} h={3.5} color="gray.400" cursor="pointer" />
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </HStack>
                                    </Td>
                                    <Td {...cellProps}>{option.deliveryString || '—'}</Td>
                                    <Td {...cellProps} isNumeric>{option.qty}</Td>
                                    <Td {...cellProps} isNumeric fontWeight="medium" color={option.adjustedListPrice != null ? 'gray.400' : 'gray.800'}>
                                        {formatPrice(option.listPrice)}
                                    </Td>
                                    <Td {...cellProps} isNumeric>
                                        {option.adjustedListPrice != null ? (
                                            <Text as="span" color="orange.500" fontWeight="medium">{formatPrice(option.adjustedListPrice)}</Text>
                                        ) : '—'}
                                    </Td>
                                    <Td {...cellProps} isNumeric>{formatPrice(option.buyPrice)}</Td>
                                    <Td
                                        {...cellProps}
                                        isNumeric
                                        color={optionMargin < 0 ? 'red.500' : isSelected || isBestMargin ? 'green.600' : 'gray.800'}
                                        fontWeight={isSelected || isBestMargin ? 'semibold' : 'normal'}
                                    >
                                        {optionMargin.toFixed(1)}%{isBestMargin ? ' ★' : ''}
                                    </Td>
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default PartOptionsCard;
